import { onUnmounted } from "vue";

/**
 * 全局视口视频空格热键。
 *
 * 对视口内 intersectionRatio 最大的带 controls 的 video，用空格切换 play/pause。
 * 跳过：输入框焦点、video 已获焦（防与原生 controls 双触发）。
 * 空格属用户手势，play() 可满足浏览器自动播放策略。
 */
export function useViewportVideoHotkey(): void {
  /** 各目标 video 的最新可见比例 */
  const ratioMap = new Map<HTMLVideoElement, number>();
  /** video → 其 IntersectionObserver，便于卸载 */
  const ioMap = new Map<HTMLVideoElement, IntersectionObserver>();

  const isEditableTarget = (el: Element | null): boolean => {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if ((el as HTMLElement).isContentEditable) return true;
    return false;
  };

  /** activeElement 为 video 或其内部节点时跳过，避免与原生 controls 双触发 */
  const isVideoFocused = (el: Element | null): boolean => {
    if (!el) return false;
    if (el instanceof HTMLVideoElement) return true;
    return Boolean(el.closest("video"));
  };

  const observeVideo = (video: HTMLVideoElement) => {
    if (!video.controls || ioMap.has(video)) return;
    if (typeof IntersectionObserver === "undefined") {
      ratioMap.set(video, 1);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const target = entry.target as HTMLVideoElement;
          if (!ioMap.has(target)) continue;
          ratioMap.set(target, entry.intersectionRatio);
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    io.observe(video);
    ioMap.set(video, io);
    ratioMap.set(video, 0);
  };

  const unobserveVideo = (video: HTMLVideoElement) => {
    const io = ioMap.get(video);
    if (io) {
      io.disconnect();
      ioMap.delete(video);
    }
    ratioMap.delete(video);
  };

  const collectFromNode = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el instanceof HTMLVideoElement) {
      observeVideo(el);
      return;
    }
    el.querySelectorAll("video[controls]").forEach((v) => {
      observeVideo(v as HTMLVideoElement);
    });
  };

  const removeFromNode = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el instanceof HTMLVideoElement) {
      unobserveVideo(el);
      return;
    }
    el.querySelectorAll("video").forEach((v) => {
      unobserveVideo(v as HTMLVideoElement);
    });
  };

  /** 取 ratio > 0 中最大者；平手取 DOM 靠后（弹层预览优先） */
  const pickTargetVideo = (): HTMLVideoElement | null => {
    let best: HTMLVideoElement | null = null;
    let bestRatio = 0;
    for (const [video, ratio] of ratioMap) {
      if (!video.isConnected || !video.controls) continue;
      if (ratio <= 0) continue;
      if (ratio > bestRatio) {
        best = video;
        bestRatio = ratio;
        continue;
      }
      if (ratio === bestRatio && best && video.compareDocumentPosition(best) & Node.DOCUMENT_POSITION_PRECEDING) {
        best = video;
      }
    }
    return best;
  };

  const togglePlay = (video: HTMLVideoElement) => {
    if (video.paused) {
      // 空格即用户手势，满足自动播放策略
      void video.play().catch((err: unknown) => {
        console.warn("[useViewportVideoHotkey] play failed:", err);
      });
    } else {
      video.pause();
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code !== "Space" && e.key !== " ") return;
    if (e.repeat) return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;

    const active = document.activeElement;
    if (isEditableTarget(active)) return;
    if (isVideoFocused(active)) return;

    const target = pickTargetVideo();
    if (!target) return;

    e.preventDefault();
    togglePlay(target);
  };

  // 首扫 + 监听后续挂载的 video
  document.querySelectorAll("video[controls]").forEach((v) => {
    observeVideo(v as HTMLVideoElement);
  });

  let mutationObserver: MutationObserver | null = null;
  if (typeof MutationObserver !== "undefined") {
    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(collectFromNode);
        mutation.removedNodes.forEach(removeFromNode);
        // controls 属性后续才挂上（如 Vue 异步渲染）
        if (mutation.type === "attributes" && mutation.target instanceof HTMLVideoElement) {
          const video = mutation.target;
          if (video.controls) {
            observeVideo(video);
          } else {
            unobserveVideo(video);
          }
        }
      }
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["controls"],
    });
  }

  window.addEventListener("keydown", onKeyDown, true);

  onUnmounted(() => {
    window.removeEventListener("keydown", onKeyDown, true);
    mutationObserver?.disconnect();
    mutationObserver = null;
    for (const video of [...ioMap.keys()]) {
      unobserveVideo(video);
    }
    ratioMap.clear();
  });
}
